import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCurrency, formatDaysUntil, jurisdictionLabel, urgencyLabel } from '../lib/format';
import { Loading, StatusPill } from '../components/Shared';

export function Timeline() {
  const [obligations, setObligations] = useState<any[] | null>(null);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    api.obligations.list().then((r) => setObligations(r.obligations));
  }, []);

  if (!obligations) return <Loading />;

  const filtered = obligations.filter((o) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return o.status !== 'completed' && o.status !== 'waived';
    if (filter === 'completed') return o.status === 'completed';
    return o.jurisdiction === filter;
  });

  // Group by year-month
  const groups: Record<string, any[]> = {};
  filtered.forEach((o) => {
    const ym = o.due_date.slice(0, 7);
    if (!groups[ym]) groups[ym] = [];
    groups[ym].push(o);
  });
  const sortedKeys = Object.keys(groups).sort();

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-eyebrow">Calendario maestro</div>
        <h1 className="page-title">Timeline</h1>
        <p className="page-description">
          Todas tus obligaciones, ordenadas cronológicamente. Federal, Wyoming y multi-estado en una sola vista.
        </p>
      </div>

      {/* Filters */}
      <div className="flex" style={{ marginBottom: '3rem', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'Todo' },
          { key: 'pending', label: 'Pendientes' },
          { key: 'completed', label: 'Completadas' },
          { key: 'federal', label: 'Federal · IRS' },
          { key: 'wyoming', label: 'Wyoming' },
        ].map(({ key, label }) => (
          <button
            key={key}
            className="btn btn-ghost"
            onClick={() => setFilter(key)}
            style={{
              borderColor: filter === key ? 'var(--accent)' : 'var(--border)',
              color: filter === key ? 'var(--accent)' : 'var(--text-dim)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="stack-lg">
        {sortedKeys.map((ym) => {
          const [year, month] = ym.split('-');
          const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
          const monthLabel = monthDate.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

          return (
            <section key={ym}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, textTransform: 'capitalize' }}>
                  {monthLabel}
                </h2>
                <span className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {groups[ym].length} obligación{groups[ym].length !== 1 && 'es'}
                </span>
              </div>
              <div className="stack-sm">
                {groups[ym].map((ob) => (
                  <ObligationCard key={ob.id} ob={ob} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ObligationCard({ ob }: { ob: any }) {
  return (
    <div
      className="card-raised"
      style={{
        display: 'grid',
        gridTemplateColumns: '80px 1fr auto',
        gap: '1.5rem',
        alignItems: 'center',
        borderLeft: `3px solid ${
          ob.urgency === 'overdue'
            ? 'var(--danger-bright)'
            : ob.urgency === 'critical'
            ? 'var(--danger)'
            : ob.urgency === 'warning'
            ? 'var(--accent)'
            : ob.urgency === 'completed'
            ? 'var(--success)'
            : 'var(--border-strong)'
        }`,
      }}
    >
      <div>
        <div className="numeric" style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400 }}>
          {new Date(ob.due_date).getDate().toString().padStart(2, '0')}
        </div>
        <div className="mono" style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
          {new Date(ob.due_date).toLocaleDateString('es-MX', { weekday: 'short' })}
        </div>
      </div>
      <div>
        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{ob.title}</div>
        <div className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
          {jurisdictionLabel(ob.jurisdiction)} · {formatDaysUntil(ob.days_until_due)}
          {ob.penalty_amount && (
            <> · multa {formatCurrency(ob.penalty_amount)}</>
          )}
        </div>
        {ob.description && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)', lineHeight: 1.6 }}>
            {ob.description}
          </p>
        )}
        {ob.learning_topic_key && (
          <Link to={`/learn/${ob.learning_topic_key}`} style={{ fontSize: '0.8rem', marginTop: '0.5rem', display: 'inline-block' }}>
            Aprender más →
          </Link>
        )}
      </div>
      <StatusPill urgency={ob.urgency} label={urgencyLabel(ob.urgency)} />
    </div>
  );
}
